
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Order, PaymentMethod } from '@/types';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  CreditCard, 
  DollarSign, 
  Wallet, 
  Banknote, 
  QrCode, 
  Plus, 
  Trash2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useIsMobile } from '@/hooks/use-mobile';
import CashPaymentDialog from './CashPaymentDialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { formatCurrency } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { clearTableData } from '@/services/orderApiService';

interface PaymentConfirmationDialogProps {
  order?: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (order: Order | undefined, paymentMethods: PaymentMethod[], paymentValues: number[]) => void;
  totalAmount?: number;
  isProcessing?: boolean;
  title?: string;
  description?: string;
}

interface PaymentEntry {
  method: PaymentMethod;
  amount: number;
}

const PaymentConfirmationDialog: React.FC<PaymentConfirmationDialogProps> = ({
  order,
  open,
  onOpenChange,
  onConfirm,
  totalAmount: propTotalAmount,
  isProcessing = false,
  title = "Confirmar Pagamento",
  description = "Adicione os métodos de pagamento para finalizar o pedido"
}) => {
  const { paymentMethods } = useApp();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [paymentEntries, setPaymentEntries] = useState<PaymentEntry[]>([]);
  const [currentAmount, setCurrentAmount] = useState<string>('');
  const [cashDialogOpen, setCashDialogOpen] = useState(false);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const [overpaidAmount, setOverpaidAmount] = useState<number>(0);
  const [cashPaymentIndex, setCashPaymentIndex] = useState<number>(-1);
  
  const isMobile = useIsMobile();
  
  const effectiveTotalAmount = propTotalAmount !== undefined ? propTotalAmount : (order?.totalAmount || 0);
  
  useEffect(() => {
    if (open) {
      setPaymentEntries([]);
      setSelectedPaymentMethod(null);
      setCurrentAmount('');
      setRemainingAmount(effectiveTotalAmount);
      setOverpaidAmount(0);
    }
  }, [open, effectiveTotalAmount]);
  
  useEffect(() => {
    const totalPaid = paymentEntries.reduce((sum, entry) => sum + entry.amount, 0);
    const remaining = effectiveTotalAmount - totalPaid;
    
    setRemainingAmount(remaining > 0 ? remaining : 0);
    setOverpaidAmount(remaining < 0 ? Math.abs(remaining) : 0);
  }, [paymentEntries, effectiveTotalAmount]);
  
  const handleAddPayment = () => {
    if (!selectedPaymentMethod) {
      toast({
        title: "Selecione um método de pagamento",
        description: "Por favor, selecione um método de pagamento antes de adicionar.",
        variant: "destructive"
      });
      return;
    }
    
    const amountValue = parseFloat(currentAmount);
    if (isNaN(amountValue) || amountValue <= 0) {
      toast({
        title: "Valor inválido",
        description: "Por favor, informe um valor válido para o pagamento.",
        variant: "destructive"
      });
      return;
    }
    
    if (selectedPaymentMethod.type === 'cash') {
      const newEntryIndex = paymentEntries.length;
      const newEntry: PaymentEntry = {
        method: selectedPaymentMethod,
        amount: amountValue
      };
      
      setPaymentEntries([...paymentEntries, newEntry]);
      setCashPaymentIndex(newEntryIndex);
      setCashDialogOpen(true);
      return;
    }
    
    setPaymentEntries([...paymentEntries, {
      method: selectedPaymentMethod,
      amount: amountValue
    }]);
    
    setCurrentAmount('');
    setSelectedPaymentMethod(null);
  };
  
  const handleRemovePayment = (index: number) => {
    const updatedEntries = [...paymentEntries];
    updatedEntries.splice(index, 1);
    setPaymentEntries(updatedEntries);
  };
  
  const handleCashPaymentConfirm = (order: Order, receivedAmount: number, change: number) => {
    if (cashPaymentIndex < 0 || cashPaymentIndex >= paymentEntries.length) {
      setCashDialogOpen(false);
      return;
    }
    
    const updatedEntries = [...paymentEntries];
    setCashDialogOpen(false);
    setCashPaymentIndex(-1);
    setCurrentAmount('');
    setSelectedPaymentMethod(null);
  };
  
  const handleConfirm = () => {
    if (paymentEntries.length === 0) {
      toast({
        title: "Nenhum pagamento informado",
        description: "Por favor, adicione pelo menos um método de pagamento.",
        variant: "destructive"
      });
      return;
    }
    
    const totalPaid = paymentEntries.reduce((sum, entry) => sum + entry.amount, 0);
    if (totalPaid < effectiveTotalAmount) {
      toast({
        title: "Valor insuficiente",
        description: `O valor total pago (${formatCurrency(totalPaid)}) é menor que o valor da venda (${formatCurrency(effectiveTotalAmount)}).`,
        variant: "destructive"
      });
      return;
    }
    
    const methods = paymentEntries.map(entry => entry.method);
    const values = paymentEntries.map(entry => entry.amount);
    
    const changeAmount = overpaidAmount;
    const receivedAmount = totalPaid;
    
    let updatedOrder = order;
    if (order) {
      updatedOrder = {
        ...order,
        receivedAmount,
        changeAmount: changeAmount,
        paymentDate: new Date(),
        status: 'paid'  // Ensure the order is marked as paid
      };
      
      // Clear table data after successful payment
      if (updatedOrder.tableId) {
        clearTableData(updatedOrder.tableId);
      }
    }
    
    // Call the onConfirm callback with the payment information
    onConfirm(updatedOrder, methods, values);
    
    // Close the dialog
    onOpenChange(false);
  };
  
  const getPaymentIcon = (type: string) => {
    switch (type) {
      case 'cash':
        return <Banknote className="h-5 w-5" />;
      case 'credit':
        return <CreditCard className="h-5 w-5" />;
      case 'debit':
        return <Wallet className="h-5 w-5" />;
      case 'pix':
        return <QrCode className="h-5 w-5" />;
      default:
        return <DollarSign className="h-5 w-5" />;
    }
  };
  
  const suggestedAmounts = [
    50, 100, remainingAmount
  ].filter(amount => amount > 0);
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className={`flex flex-col ${isMobile ? "w-[90vw] max-w-[90vw] p-4" : ""}`}
          style={{ maxHeight: '90vh' }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-hidden">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center py-2 border-b">
                <div className="text-sm text-muted-foreground">Valor Total</div>
                <div className="text-xl font-bold">
                  {formatCurrency(effectiveTotalAmount)}
                </div>
              </div>
              <div className="text-center py-2 border-b">
                <div className="text-sm text-muted-foreground">Falta Pagar</div>
                <div className={`text-xl font-bold ${remainingAmount > 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {remainingAmount > 0 
                    ? formatCurrency(remainingAmount) 
                    : 'Pago'}
                </div>
              </div>
            </div>
            
            {paymentEntries.length > 0 && (
              <div className="border rounded-md p-3 bg-muted/30">
                <h3 className="font-medium text-sm mb-2">Formas de Pagamento Adicionadas</h3>
                <div className="space-y-2">
                  {paymentEntries.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between border-b pb-2">
                      <div className="flex items-center gap-2">
                        <div className={`p-1 rounded-full bg-primary text-white`}>
                          {getPaymentIcon(entry.method.type || 'other')}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{entry.method.name}</div>
                          <div className="text-xs text-muted-foreground">{formatCurrency(entry.amount)}</div>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemovePayment(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                {overpaidAmount > 0 && (
                  <div className="mt-2 p-2 bg-green-100 rounded-md flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-700">Troco: {formatCurrency(overpaidAmount)}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="border rounded-md p-3">
              <h3 className="font-medium text-sm mb-2">Adicionar Forma de Pagamento</h3>
              
              <div className="space-y-3">
                <ScrollArea className="h-24 pr-4">
                  <div className="grid grid-cols-2 gap-2">
                    {paymentMethods.map(method => (
                      <div
                        key={method.id}
                        className={`p-2 border rounded-lg cursor-pointer transition-all ${
                          selectedPaymentMethod?.id === method.id 
                            ? 'border-primary bg-primary/5' 
                            : 'hover:border-gray-400'
                        }`}
                        onClick={() => setSelectedPaymentMethod(method)}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`p-1 rounded-full ${
                            selectedPaymentMethod?.id === method.id 
                              ? 'bg-primary text-white' 
                              : 'bg-muted'
                          }`}>
                            {getPaymentIcon(method.type || 'other')}
                          </div>
                          <div className="text-sm">{method.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                <div className="grid gap-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Valor"
                      value={currentAmount}
                      onChange={(e) => setCurrentAmount(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      variant="outline" 
                      type="button"
                      disabled={!selectedPaymentMethod || remainingAmount <= 0}
                      onClick={() => setCurrentAmount(remainingAmount.toFixed(2).toString())}
                    >
                      Faltante
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {suggestedAmounts.map((amount, index) => (
                      <Button 
                        key={index}
                        variant="outline" 
                        size="sm"
                        type="button" 
                        onClick={() => setCurrentAmount(amount.toFixed(2).toString())}
                        disabled={!selectedPaymentMethod}
                      >
                        {formatCurrency(amount)}
                      </Button>
                    ))}
                  </div>
                  
                  <Button
                    onClick={handleAddPayment}
                    disabled={!selectedPaymentMethod || !currentAmount}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Pagamento
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-4 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={paymentEntries.length === 0 || remainingAmount > 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>Confirmar Pagamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {selectedPaymentMethod && selectedPaymentMethod.type === 'cash' && (
        <CashPaymentDialog
          order={{
            ...order || { id: '', tableId: '', status: 'open' as const, type: 'dine-in' as const, items: [], createdAt: new Date(), updatedAt: new Date(), totalAmount: 0 },
            totalAmount: parseFloat(currentAmount) || 0
          }}
          open={cashDialogOpen}
          onOpenChange={setCashDialogOpen}
          onConfirm={handleCashPaymentConfirm}
        />
      )}
    </>
  );
};

// Helper function that should be preserved
const getPaymentIcon = (type: string) => {
  switch (type) {
    case 'cash':
      return <Banknote className="h-5 w-5" />;
    case 'credit':
      return <CreditCard className="h-5 w-5" />;
    case 'debit':
      return <Wallet className="h-5 w-5" />;
    case 'pix':
      return <QrCode className="h-5 w-5" />;
    default:
      return <DollarSign className="h-5 w-5" />;
  }
};

export default PaymentConfirmationDialog;
